import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpenseDetailDrawer } from "@/components/expenses/ExpenseDetailDrawer";
import { FilterBar } from "@/components/expenses/FilterBar";
import { ExpensesTable } from "@/components/expenses/ExpensesTable";
import { ExpensesHeader } from "@/components/expenses/ExpensesHeader";
import { BulkToolbar } from "@/components/expenses/BulkToolbar";
import { ColumnManager } from "@/components/expenses/ColumnManager";
import { ViewSwitch } from "@/components/expenses/ViewSwitch";
import { ApprovalsKanban } from "@/components/expenses/ApprovalsKanban";
import { CreateExpenseDialog } from "@/components/expenses/CreateExpenseDialog";
import { AddReceiptDialog } from "@/components/expenses/AddReceiptDialog";
import { DataMovementDialog } from "@/components/expenses/DataMovementDialog";
import { ReceiptDialog } from "@/components/expenses/ReceiptDialog";
import { ArchiveView } from "@/components/expenses/ArchiveView";
import { COLUMN_DEFS, loadColumnConfig, persistColumnConfig } from "@/components/expenses/columns";
import { useExpensesData, EMPTY_FILTERS, URL_KEYS, parseFiltersFromParams } from "@/hooks/useExpensesData";
import { Maximize2, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const dmToast = (dm, base) => {
  if (dm?.mode === "exported") {
    toast.success(`${base} · auto-${dm.archived ? "exported & archived" : "exported"} to ${dm.destination}`);
  } else if (dm?.mode === "queued") {
    toast.success(`${base} · queued for ${dm.frequency} export to ${dm.destination}`);
  } else {
    toast.success(base);
  }
};

export default function Expenses() {
  const location = useLocation();
  const navigate = useNavigate();
  const list = useExpensesData();

  const [defaultView, setDefaultViewState] = useState(() => localStorage.getItem("expenses_default_view") || "table");
  const [view, setView] = useState(() => localStorage.getItem("expenses_default_view") || "table");
  const setDefaultView = (v) => { localStorage.setItem("expenses_default_view", v); setDefaultViewState(v); };

  const [columnConfig, setColumnConfig] = useState(loadColumnConfig);
  const updateColumns = (next) => { setColumnConfig(next); persistColumnConfig(next); };
  const visibleCols = columnConfig.filter((c) => c.visible).map((c) => COLUMN_DEFS.find((d) => d.key === c.key));

  const [mode, setMode] = useState("active");
  const [archiveTab, setArchiveTab] = useState("exported");
  const [archiveCounts, setArchiveCounts] = useState({});
  const [assignedView, setAssignedView] = useState(false);
  const [tableFs, setTableFs] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const [board, setBoard] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const searchRef = useRef(null);

  // deep links + Aria navigation
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("mode") === "archive") {
      setMode("archive");
      navigate("/expenses", { replace: true });
      return;
    }
    if (!URL_KEYS.some((k) => sp.has(k))) return;
    list.setFilters(parseFiltersFromParams(sp));
    if (sp.get("q")) list.setSearchImmediate(sp.get("q"));
    setMode("active");
    setView("table");
    if (sp.get("suggest_save") === "1") {
      setTimeout(() => toast.info("Aria filtered this view for you", {
        description: "Save it to reuse anytime.",
        action: { label: "Save as view", onClick: () => setSaveOpen(true) },
        duration: 9000,
      }), 500);
    }
    navigate("/expenses", { replace: true });
  }, [location.search, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/workspace/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tableFs) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setTableFs(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tableFs]);

  // "/" focuses search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const loadBoard = useCallback(() => {
    api.get("/approvals/kanban").then((r) => setBoard(r.data)).catch(() => toast.error("Could not load the board"));
  }, []);

  useEffect(() => {
    if (view === "kanban") loadBoard();
  }, [view, loadBoard]);

  useEffect(() => {
    const onActed = () => { if (view === "kanban") loadBoard(); };
    window.addEventListener("aria:acted", onActed);
    return () => window.removeEventListener("aria:acted", onActed);
  }, [view, loadBoard]);

  const drawerFilter = (key, value) => {
    setView("table");
    if (key === "date") list.setFilters((f) => ({ ...f, date_from: value, date_to: value }));
    else list.setFilters((f) => ({ ...f, [key]: [value] }));
    setDrawerId(null);
  };

  const applyView = (v) => { list.applyView(v); setView("table"); };

  const allChecked = list.data.items.length > 0 && list.data.items.every((e) => selected.has(e.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) list.data.items.forEach((e) => next.delete(e.id));
    else list.data.items.forEach((e) => next.add(e.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await api.post("/expenses/bulk", { ids, action });
      const base =
        action === "approve" ? `Approved ${ids.length} expense${ids.length > 1 ? "s" : ""}` :
        action === "reject" ? `Rejected ${ids.length}` :
        action === "export" ? `Exported ${ids.length}` :
        `Aria messaged ${ids.length} employee${ids.length > 1 ? "s" : ""}`;
      dmToast(r.data.data_movement, base);
      setSelected(new Set());
      list.refresh();
    } catch {
      toast.error("Bulk action failed");
    }
  };

  const assignTo = async (memberId) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await api.post("/expenses/assign", { ids, assignee_id: memberId });
      toast.success(memberId ? `Assigned ${r.data.updated} to ${r.data.assigned_to.name}` : `Unassigned ${r.data.updated}`);
      setSelected(new Set());
      list.refresh();
    } catch {
      toast.error("Assignment failed");
    }
  };

  const rowAction = async (id, action) => {
    try {
      const r = await api.post("/expenses/bulk", { ids: [id], action });
      const base = action === "approve" ? "Approved" : action === "reject" ? "Rejected" : action === "export" ? "Exported" : action === "message" ? "Aria messaged the employee" : action === "restore" ? "Restored to review" : "Archived";
      dmToast(r.data.data_movement, base);
      list.refresh();
    } catch {
      toast.error("Action failed");
    }
  };

  const moveCard = async (id, from, to) => {
    setBoard((b) => {
      const next = { ...b };
      const card = (next[from] || []).find((c) => c.id === id);
      if (!card) return b;
      next[from] = next[from].filter((c) => c.id !== id);
      next[to] = [card, ...(next[to] || [])];
      return next;
    });
    try {
      const r = await api.post(`/approvals/${id}/move`, { to });
      if (r.data.data_movement) {
        dmToast(r.data.data_movement, "Approved");
        loadBoard();
      } else {
        toast.success(to === "approved" ? "Approved" : "Moved");
      }
    } catch {
      toast.error("Move failed — reverting");
      loadBoard();
    }
  };

  return (
    <div className={tableFs ? "fixed inset-0 z-50 flex flex-col gap-4 bg-[#0A0A0A] p-4 sm:p-6" : "relative flex h-full min-h-0 flex-col gap-4"} data-testid="expenses-page">
      <ExpensesHeader
        mode={mode} setMode={setMode}
        tab={list.tab} setTab={(k) => { list.setTab(k); if (view !== "table") setView("table"); }}
        view={view} setView={setView} tabCounts={list.data.tab_counts}
        archiveTab={archiveTab} setArchiveTab={setArchiveTab} archiveCounts={archiveCounts}
        onDataMovement={() => setDmOpen(true)} onAddReceipt={() => setReceiptOpen(true)} onCreate={() => setCreateOpen(true)}
      />

      {mode === "archive" ? (
        <ArchiveView tab={archiveTab} onCounts={setArchiveCounts} />
      ) : (
        <>
          {/* view switch + filter bar */}
          <div className="flex flex-wrap items-center gap-2">
            <ViewSwitch view={view} onChange={setView} defaultView={defaultView} onDefaultChange={setDefaultView} />
            {view === "table" ? (
              <FilterBar
                search={list.search} onSearch={list.setSearch} searchRef={searchRef}
                filters={list.filters} onFilterChange={list.onFilterChange} onClearFilter={list.clearFilterChip}
                options={list.options} views={list.views} onApplyView={applyView} onSaveView={list.saveView} onDeleteView={list.deleteView}
                saveOpen={saveOpen} setSaveOpen={setSaveOpen} total={list.data.total}
                assignedView={assignedView} onToggleAssigned={() => setAssignedView((v) => !v)}
              />
            ) : (
              <span className="text-[12px] text-[#6b6b6b]" data-testid="expenses-queue-summary">
                {(board?.needs_review || []).length > 0
                  ? `${(board?.needs_review || []).length} need a human decision — Aria handled the rest.`
                  : "Queue clear — Aria has it handled."}
              </span>
            )}
          </div>

          {view === "kanban" ? (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {!board ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-96 rounded-[14px] bg-[#141414]" />)}
                </div>
              ) : (
                <ApprovalsKanban board={board} onMove={moveCard} onOpen={setDrawerId} onQuickAction={rowAction}
                  onDataMovement={() => setDmOpen(true)} />
              )}
            </div>
          ) : (
            <>
              <BulkToolbar selected={selected} members={members} onBulk={bulk} onAssign={assignTo}
                onClear={() => setSelected(new Set())} />
              <ExpensesTable
                cols={visibleCols} data={list.data} loading={list.loading}
                sort={list.sort} dir={list.dir} onSort={list.setSorting}
                selected={selected} allChecked={allChecked} toggleAll={toggleAll} toggleOne={toggleOne}
                onOpen={setDrawerId} onRowAction={rowAction} onReceipt={setReceiptId}
                page={list.page} totalPages={list.totalPages} setPage={list.setPage}
                grouped={list.tab === "active" && !assignedView}
                filters={list.filters} onFilterChange={list.onFilterChange} options={list.options}
                assignedView={assignedView}
                headerActions={
                  <>
                    <ColumnManager config={columnConfig} onChange={updateColumns} compact />
                    <button onClick={() => setTableFs((f) => !f)} data-testid="expenses-table-expand"
                      title={tableFs ? "Exit full screen" : "Expand table full screen"}
                      className="flex h-6 w-6 items-center justify-center rounded-md text-[#6b6b6b] transition-colors hover:bg-[#1f1f1f] hover:text-white">
                      {tableFs ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                    </button>
                  </>
                }
              />
            </>
          )}
        </>
      )}

      <ExpenseDetailDrawer expenseId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} onFilter={drawerFilter}
        onChanged={list.refresh} />
      <ReceiptDialog expenseId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} onOpenDetails={setDrawerId} />
      <CreateExpenseDialog open={createOpen} onOpenChange={setCreateOpen} employees={list.employeeOptions} onCreated={list.refresh} />
      <AddReceiptDialog open={receiptOpen} onOpenChange={setReceiptOpen} onCreated={list.refresh} />
      <DataMovementDialog open={dmOpen} onOpenChange={setDmOpen} />
    </div>
  );
}
